import { makeAutoObservable, observable } from "mobx";
import { IAPIGetListInterestTagResponseModel } from "../interfaces/IAPIListInterestTagModel";
import { ListInterestTagModel } from "../models";
import {  ListInterestTagService } from "../services";
import { AuthService, IOption } from "@mindmemobile/mm-shared-global";
import { firstValueFrom } from "rxjs";

export default class ListInterestTagStore {
  @observable lists: ListInterestTagModel[] = [];
  @observable interests: ListInterestTagModel[] = [];
  @observable tags: ListInterestTagModel[] = [];
  @observable apiPending = false;

  constructor() {
    makeAutoObservable(this);
    this.loadData();
  }

  loadData(): void {
    if (this.apiPending) {
      return;
    }
    // accountId arrives asynchronously (the host app posts it in) and is
    // localStorage-backed rather than observable, so it can still be empty when
    // this store is built: CheckOutStore owns it and is constructed at MODULE
    // SCOPE in ItemDetailPageMain, i.e. the moment that chunk is imported.
    // Whether auth has landed by then is a race, which is why the failure was
    // intermittent. Requesting with an empty accountId is a guaranteed 400, so
    // skip it and let a consumer call loadData() again once auth is present.
    // Same guard AuthService.hasAccount that ProductStore and
    // FundraiserStore.fetchListInterestTags already use.
    if (!AuthService.hasAccount) {
      return;
    }
    this.apiPending = true;
    ListInterestTagService.getMindMeListInterestTag(
      AuthService.authData.accountId
    ).subscribe({
      next: (response) => {
        this.deserializeAPIResponse(response.result);
        this.apiPending = false;
      },
      // Without an error handler RxJS reports the rejection as an unhandled
      // error, and apiPending stayed true forever - so a single failure
      // permanently wedged this store (a module-scope singleton) and the list,
      // tag and interest dropdowns stayed empty for the rest of the session.
      error: () => {
        this.apiPending = false;
      },
    });
  }

  deserializeAPIResponse(data: IAPIGetListInterestTagResponseModel): void {
    this.lists =
      data?.lists?.map((l: any) => ListInterestTagModel.deserialize(l)) || [];
    this.interests =
      data?.interests?.map((l: any) => ListInterestTagModel.deserialize(l)) ||
      [];
    this.tags =
      data?.tags?.map((l: any) => ListInterestTagModel.deserialize(l)) || [];
  }

  get listOptions(): IOption[] {
    return this.lists.map((l) => ({ label: l.name, value: l.id }));
  }

  get tagOptions(): IOption[] {
    return this.tags.map((l) => ({ label: l.name, value: l.id }));
  }

  get interestOptions(): IOption[] {
    return this.interests.map((l) => ({ label: l.name, value: l.id }));
  }

  async createNew(name: string, type: string): Promise<string> {
    const response = await firstValueFrom(
      ListInterestTagService.create(AuthService.authData.accountId, name, type)
    );
    if (response.result?.id) {
      this.loadData(); 
      return response.result.id;
    }
    return "";
  }
}
